import { skipToken } from '@reduxjs/toolkit/query'

import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { useGetNotesQuery } from '../../clientNotes/data/notesApi'
import { isIntakeNote } from '../../clientNotes/utils/npSessionNote'
import { useClientAppointmentsData } from '../../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { NpTestPlanModality } from '../data/npTestPlanTypes'

export interface NpTestPlanScheduledModality {
  /** Virtual until the intake appointment says otherwise, so the form always has a default. */
  scheduledModality: NpTestPlanModality
  isLoading: boolean
}

const IN_PERSON_MEETING_FORMATS: string[] = [MEETING_FORMATS.IN_PERSON, MEETING_FORMATS.IN_PERSON_UNDERSCORE]

/**
 * The modality the build form starts from. No episode- or patient-level modality
 * exists yet (ENGBEND-12885), so this reads the appointment the episode's intake note was written
 * for. The provider is warned, not blocked, when they pick something else.
 */
export const useNpTestPlanScheduledModality = (episodeId?: string): NpTestPlanScheduledModality => {
  // Same args as the entrypoint's intake-note gate, so this is served from its cache.
  const { data: notes = [], isLoading: isLoadingNotes } = useGetNotesQuery(episodeId ? { episodeId } : skipToken)
  const intakeAppointmentId = notes.find((note) => isIntakeNote(note) && note.appointment_id)?.appointment_id

  // There is no GET for a single appointment, so the intake one is picked from the episode's list,
  // kept out of redux so the rest of the chart's appointment list is not narrowed to this episode.
  const { data: appointments = [], isLoading: isLoadingAppointments } = useClientAppointmentsData(
    { episodeId, skipReduxDispatch: true },
    { skip: !intakeAppointmentId },
  )
  const intakeAppointment = appointments.find(({ appointmentId }) => appointmentId === intakeAppointmentId)

  return {
    scheduledModality: IN_PERSON_MEETING_FORMATS.includes(intakeAppointment?.meetingFormat ?? '')
      ? NpTestPlanModality.IN_PERSON
      : NpTestPlanModality.VIRTUAL,
    isLoading: Boolean(episodeId) && (isLoadingNotes || isLoadingAppointments),
  }
}
